/**
 * HelpContext — Shared state for help tickets and feature requests
 *
 * Bridges the user-facing HelpPage and the AdminPanel so that:
 * - User submits a ticket → it appears in the admin panel
 * - Admin changes status or adds a response → user sees it live
 *
 * API_TODO: Replace with backend API calls:
 *   POST /api/help/submit
 *   GET  /api/help/tickets
 *   PUT  /api/help/tickets/:id { status, adminResponse }
 *   GET  /api/help/feature-requests
 *   PUT  /api/help/feature-requests/:id { status, adminResponse }
 */
import { createContext, useContext, useEffect, useState } from 'react';

const HelpContext = createContext(null);

// ── Seed data (same as what was in AdminPanel mock) ──
const INITIAL_HELP_TICKETS = [
  {
    id: 'h1', type: 'qr_not_scanning', typeLabel: 'QR code not scanning properly',
    description: 'My QR code doesn\'t scan on older Android phones. Tried multiple times with Samsung Galaxy S10.',
    submittedAt: '2026-05-20T10:30:00Z', status: 'open', user: 'Sarah Chen', adminResponse: '',
  },
  {
    id: 'h2', type: 'photo_upload', typeLabel: 'Photo upload failing',
    description: 'Getting an error when I try to upload a HEIC format photo from my iPhone.',
    submittedAt: '2026-05-19T14:15:00Z', status: 'resolved', user: 'Marco Bellini',
    adminResponse: 'We\'ve added HEIC support in the latest update. Please try uploading again and let us know if the issue persists.',
    resolvedAt: '2026-05-20T09:00:00Z', respondedAt: '2026-05-20T09:00:00Z',
  },
  {
    id: 'h3', type: 'wallet_pass', typeLabel: 'Wallet pass not working (Apple/Google)',
    description: 'Apple Wallet pass shows blank after adding. Tried removing and re-adding.',
    submittedAt: '2026-05-18T09:00:00Z', status: 'in_progress', user: 'Priya Sharma',
    adminResponse: 'We\'re investigating this issue with Apple Wallet. A fix is being tested and should be deployed within 48 hours.',
    startedAt: '2026-05-19T10:00:00Z', respondedAt: '2026-05-19T10:00:00Z',
  },
];

const INITIAL_FEATURE_REQUESTS = [
  {
    id: 'fr1', type: 'feature_request', typeLabel: 'New Feature Request',
    description: 'Would love to have analytics showing which of my links get the most clicks after someone scans my QR.',
    submittedAt: '2026-05-21T16:00:00Z', status: 'open', user: 'Jonas Weber', adminResponse: '',
  },
  {
    id: 'fr2', type: 'feature_request', typeLabel: 'New Feature Request',
    description: 'Dark mode support for the scan page preview would be great for evening events.',
    submittedAt: '2026-05-20T11:45:00Z', status: 'in_progress', user: 'Elena Rodriguez',
    adminResponse: 'Great idea! We\'re actively working on dark mode support. Expect it in the next update.',
    startedAt: '2026-05-21T10:00:00Z', respondedAt: '2026-05-21T10:00:00Z',
  },
  {
    id: 'fr3', type: 'feature_request', typeLabel: 'New Feature Request',
    description: 'Option to add a video introduction that plays when someone scans my code.',
    submittedAt: '2026-05-17T08:30:00Z', status: 'dismissed', user: 'Raj Patel',
    adminResponse: 'Thanks for the suggestion! Video intros are outside our current scope, but we\'ll revisit this in a future roadmap review.',
    dismissedAt: '2026-05-18T14:00:00Z', respondedAt: '2026-05-18T14:00:00Z',
  },
];

export function HelpProvider({ children }) {
  const [helpTickets, setHelpTickets] = useState(() => {
    const saved = localStorage.getItem('connektedin_help_tickets');
    return saved ? JSON.parse(saved) : INITIAL_HELP_TICKETS;
  });
  const [featureRequests, setFeatureRequests] = useState(() => {
    const saved = localStorage.getItem('connektedin_feature_requests');
    return saved ? JSON.parse(saved) : INITIAL_FEATURE_REQUESTS;
  });

  useEffect(() => {
    localStorage.setItem('connektedin_help_tickets', JSON.stringify(helpTickets));
  }, [helpTickets]);

  useEffect(() => {
    localStorage.setItem('connektedin_feature_requests', JSON.stringify(featureRequests));
  }, [featureRequests]);

  // ── User-side actions ──

  const submitTicket = (ticket) => {
    if (ticket.type === 'feature_request') {
      setFeatureRequests(prev => [...prev, ticket]);
    } else {
      setHelpTickets(prev => [...prev, ticket]);
    }
  };

  // ── Admin-side actions (Help Tickets) ──

  const startTicket = (id) => {
    setHelpTickets(prev => prev.map(t =>
      t.id === id ? { ...t, status: 'in_progress', startedAt: new Date().toISOString() } : t
    ));
  };

  const resolveTicket = (id) => {
    setHelpTickets(prev => prev.map(t =>
      t.id === id ? { ...t, status: 'resolved', resolvedAt: new Date().toISOString() } : t
    ));
  };

  const reopenTicket = (id) => {
    setHelpTickets(prev => prev.map(t =>
      t.id === id ? { ...t, status: 'open', resolvedAt: undefined, startedAt: undefined } : t
    ));
  };

  const saveAdminResponse = (id, text) => {
    setHelpTickets(prev => prev.map(t =>
      t.id === id ? { ...t, adminResponse: text, respondedAt: new Date().toISOString() } : t
    ));
  };

  // ── Admin-side actions (Feature Requests) ──

  const startFeatureRequest = (id) => {
    setFeatureRequests(prev => prev.map(fr =>
      fr.id === id ? { ...fr, status: 'in_progress', startedAt: new Date().toISOString() } : fr
    ));
  };

  const dismissFeatureRequest = (id) => {
    setFeatureRequests(prev => prev.map(fr =>
      fr.id === id ? { ...fr, status: 'dismissed', dismissedAt: new Date().toISOString() } : fr
    ));
  };

  const reopenFeatureRequest = (id) => {
    setFeatureRequests(prev => prev.map(fr =>
      fr.id === id ? { ...fr, status: 'open', dismissedAt: undefined, startedAt: undefined } : fr
    ));
  };

  const saveFeatureRequestResponse = (id, text) => {
    setFeatureRequests(prev => prev.map(fr =>
      fr.id === id ? { ...fr, adminResponse: text, respondedAt: new Date().toISOString() } : fr
    ));
  };

  // Derived lists — Help Tickets
  const openTickets = helpTickets.filter(t => t.status === 'open');
  const inProgressTickets = helpTickets.filter(t => t.status === 'in_progress');
  const resolvedTickets = helpTickets.filter(t => t.status === 'resolved');

  // Derived lists — Feature Requests
  const openFeatureRequests = featureRequests.filter(fr => fr.status === 'open');
  const inProgressFeatureRequests = featureRequests.filter(fr => fr.status === 'in_progress');
  const dismissedFeatureRequests = featureRequests.filter(fr => fr.status === 'dismissed');

  return (
    <HelpContext.Provider value={{
      helpTickets, featureRequests,
      openTickets, inProgressTickets, resolvedTickets,
      openFeatureRequests, inProgressFeatureRequests, dismissedFeatureRequests,
      submitTicket, startTicket, resolveTicket, reopenTicket, saveAdminResponse,
      startFeatureRequest, dismissFeatureRequest, reopenFeatureRequest, saveFeatureRequestResponse,
      setFeatureRequests,
    }}>
      {children}
    </HelpContext.Provider>
  );
}

export function useHelp() {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within a HelpProvider');
  return ctx;
}
